import { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { pushDataTrackerPageView } from 'approot/shared/data-tracker/data-tracker-push-pageview';
import { UserFragment_userClasses } from 'approot/shared/user/__generated__/UserFragment';
import { GetCurrentUserClass } from './__generated__/GetCurrentUserClass';
import {
  GET_CREATE_CLASS_REMINDER,
  MARK_CLASS_AS_USED,
  SET_CLASS_DEFAULT,
} from './classes.graphql';
import { GET_CURRENT_USER_CLASS } from './classes.apollo';
import {
  MarkClassAsUsed,
  MarkClassAsUsedVariables,
} from './__generated__/MarkClassAsUsed';
import {
  SetClassDefault,
  SetClassDefaultVariables,
} from './__generated__/SetClassDefault';
import { GetCreateClassReminder } from 'approot/classes/__generated__/GetCreateClassReminder';

export const useCurrentClassVar = () => {
  const { data } = useQuery<GetCurrentUserClass>(GET_CURRENT_USER_CLASS);
  return data?.currentUserClass;
};

export const useClassesPageView = () => {
  const hasCurrentClass = !!useCurrentClassVar();

  useEffect(() => {
    if (hasCurrentClass) {
      pushDataTrackerPageView();
    }
  }, [hasCurrentClass]);
};

export const useClassesMarkAsUsed = (
  userClass: UserFragment_userClasses,
  onError?: () => void,
  optimisticResponse?: MarkClassAsUsed
) => {
  const [markClassAsUsedMutation] = useLoggingMutation<
    MarkClassAsUsed,
    MarkClassAsUsedVariables
  >(MARK_CLASS_AS_USED, {
    onError: () => onError?.(),
  });

  const markClassAsUsed = useCallback(() => {
    markClassAsUsedMutation({
      variables: {
        input: {
          classId: userClass.id,
        },
      },
      optimisticResponse: optimisticResponse || {
        markClassAsUsed: {
          ...userClass,
          lastMarkedAsUsed: new Date(),
        },
      },
    }).catch(() => {});
  }, [userClass, markClassAsUsedMutation, optimisticResponse]);

  return {
    markClassAsUsed,
  };
};

export const useSetClassDefault = () => {
  const [setClassDefault] = useLoggingMutation<
    SetClassDefault,
    SetClassDefaultVariables
  >(SET_CLASS_DEFAULT);

  return {
    setClassDefault: (classId: number) => {
      setClassDefault({
        variables: {
          input: {
            classId,
          },
        },
      });
    },
  };
};

export const useGetCreateClassReminder = () => {
  const { refetch } = useLoggingQuery<GetCreateClassReminder, null>(
    GET_CREATE_CLASS_REMINDER,
    {
      skip: true,
      fetchPolicy: 'cache-and-network',
    }
  );
  return refetch;
};
